import React, { useEffect, useRef, useImperativeHandle, forwardRef, useCallback } from 'react';
import { MapContainer, TileLayer, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet-draw';
import 'leaflet.markercluster';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import '../styles/leaflet-custom.css';
import { GridElement, Connection, ElementAction } from '../types';
import { MeterRealtimeMetrics } from '../hooks/useMeterRealtimeMetrics';
import {
  MeterLiveMetric,
  MeterLiveMetricsMap,
  LuminaireLiveMetric,
  LuminaireLiveMetricsMap,
  MetricsVisibility,
} from '../types/presenter.types';

const DEFAULT_METRICS_VISIBILITY: MetricsVisibility = {
  voltagePower: true,
  rssi: true,
  statusRing: true,
};

const EMPTY_METER_LIVE_MAP: MeterLiveMetricsMap = new Map();
const EMPTY_LUMINAIRE_LIVE_MAP: LuminaireLiveMetricsMap = new Map();

// Échappe les champs de données injectés dans du HTML (icônes/tooltips/popups).
const escapeHtml = (value: unknown): string =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const formatMeterPower = (watts: number | null): { text: string; unit: string } | null => {
  if (watts === null) return null;
  if (Math.abs(watts) >= 1000) return { text: (watts / 1000).toFixed(2), unit: 'kW' };
  return { text: watts.toFixed(0), unit: 'W' };
};

const formatLightPower = (watts: number | null): string | null => {
  if (watts === null) return null;
  if (Math.abs(watts) >= 1000) return `${(watts / 1000).toFixed(2)} kW`;
  return `${watts.toFixed(0)} W`;
};

const rssiBars = (rssi: number | null): number => {
  if (rssi === null) return 0;
  if (rssi >= -70) return 4;
  if (rssi >= -85) return 3;
  if (rssi >= -100) return 2;
  if (rssi >= -115) return 1;
  return 0;
};

const rssiBarsHtml = (rssi: number | null): string => {
  const active = rssiBars(rssi);
  const colors = ['#94a3b8', '#94a3b8', '#94a3b8', '#94a3b8'];
  for (let i = 0; i < active; i += 1) {
    colors[i] = active >= 3 ? '#16a34a' : active === 2 ? '#f59e0b' : '#ef4444';
  }
  return `
    <span style="display:inline-flex;align-items:flex-end;gap:1px;height:10px;">
      ${[3, 5, 7, 9]
        .map((h, i) => `<span style="width:2px;height:${h}px;background:${colors[i]};border-radius:1px;"></span>`)
        .join('')}
    </span>
  `;
};

const buildMeterBubbleHtml = (
  live: MeterLiveMetric | undefined,
  vis: MetricsVisibility,
): string => {
  if (!live) return '';
  if (!vis.voltagePower) return '';

  const voltage = live.voltage !== null ? `${live.voltage.toFixed(0)} V` : '— V';
  const powerFmt = formatMeterPower(live.power);
  const power = powerFmt ? `${powerFmt.text} ${powerFmt.unit}` : '— W';

  return `
    <div style="
      display:inline-flex;align-items:center;gap:6px;
      padding:2px 6px;border-radius:10px;
      background:rgba(255,255,255,0.96);
      border:1px solid #e2e8f0;
      font-size:10px;line-height:1;color:#0f172a;
      box-shadow:0 2px 4px rgba(0,0,0,0.12);
      white-space:nowrap;
    ">
      <span style="font-weight:600;">${voltage}</span>
      <span style="color:#94a3b8;">·</span>
      <span style="font-weight:600;">${power}</span>
    </div>
  `;
};

const buildLightBubbleHtml = (
  live: LuminaireLiveMetric | undefined,
  vis: MetricsVisibility,
): string => {
  if (!live) return '';
  const parts: string[] = [];
  if (vis.voltagePower) {
    const power = formatLightPower(live.power) ?? '— W';
    parts.push(`<span style="font-weight:600;">${power}</span>`);
  }
  if (vis.rssi) {
    const rssiLabel = live.rssi !== null ? `${live.rssi.toFixed(0)} dBm` : '— dBm';
    parts.push(`
      <span style="display:inline-flex;align-items:center;gap:3px;">
        ${rssiBarsHtml(live.rssi)}
        <span style="color:#475569;">${rssiLabel}</span>
      </span>
    `);
  }
  if (parts.length === 0) return '';

  return `
    <div style="
      display:inline-flex;align-items:center;gap:6px;
      padding:2px 6px;border-radius:10px;
      background:rgba(255,255,255,0.96);
      border:1px solid #e2e8f0;
      font-size:10px;line-height:1;color:#0f172a;
      box-shadow:0 2px 4px rgba(0,0,0,0.12);
      white-space:nowrap;
    ">
      ${parts.join('<span style="color:#94a3b8;">·</span>')}
    </div>
  `;
};

// Fix for default markers in Leaflet with Webpack
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// ---------------------------------------------------------------------------
// Helpers de rendu d'icônes (pures — réutilisées pour le build et le live)
// ---------------------------------------------------------------------------

const getMeterLocalizationStatus = (element: GridElement): 'good' | 'unlocalized' | 'normal' => {
  if (element.type !== 'meter') return 'normal';
  const lat = element.gpsCoordinates?.latitude ?? 0;
  const lng = element.gpsCoordinates?.longitude ?? 0;
  if (lat === 0 && lng === 0) return 'unlocalized';
  return 'good';
};

const getElementStatusColor = (
  element: GridElement,
  live: MeterLiveMetric | undefined,
  vis: MetricsVisibility,
): string => {
  // Cas 1 (prioritaire): anneau online/offline live quand le toggle est actif.
  if (element.type === 'meter' && vis.statusRing) {
    return live?.isOnline ? '#10b981' : '#ef4444';
  }
  // Cas 2: compteur Jouly verrouillé → violet
  if (element.type === 'meter' && element.isJouly) return '#7c3aed';
  // Cas 3: compteur non localisé → orange
  if (element.type === 'meter' && getMeterLocalizationStatus(element) === 'unlocalized') return '#f97316';
  switch (element.status) {
    case 'ACTIVE': return '#10b981';
    case 'INACTIVE': return '#6b7280';
    case 'MAINTENANCE': return '#f59e0b';
    case 'OFFLINE': return '#ef4444';
    default: return '#6b7280';
  }
};

const getIconSVG = (iconName: string, color: string): string => {
  switch (iconName) {
    case 'plug':
      return `<svg width="20" height="24" viewBox="0 0 24 24" fill="${color}" xmlns="http://www.w3.org/2000/svg">
<mask id="mask0_4005_2129" maskUnits="userSpaceOnUse" x="0" y="0" width="20" height="20">
<path d="M0 0H24V24H0V0Z" fill="white"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M16.6154 10.1538H14.1538L15.6923 6.46154C15.7546 6.33077 15.7785 6.18846 15.7654 6.04692C15.7523 5.90538 15.7028 5.76846 15.6215 5.64923C15.5402 5.53 15.4298 5.43231 15.3008 5.36462C15.1718 5.29692 15.0285 5.26154 14.8846 5.26154H10.8846C10.7108 5.26138 10.5408 5.31077 10.3923 5.40462C10.2438 5.49846 10.1231 5.63308 10.0431 5.79385L7.54308 11.7938C7.48077 11.9246 7.45846 12.0692 7.47154 12.2108C7.48462 12.3523 7.53231 12.4869 7.61077 12.6008C7.68923 12.7146 7.79538 12.8031 7.91846 12.8577C8.04154 12.9123 8.17769 12.9308 8.31154 12.9115H9.31154L7.04308 18.9115C6.70077 19.8 7.8 20.5 8.5 19.9L12.5 16.4L16.5 12.9C17.1 12.4 16.8 11.1 16.6154 10.1538ZM12.1154 11.1538C12.0531 11.2846 12.0308 11.4277 12.0438 11.5692C12.0569 11.7108 12.1054 11.8469 12.1862 11.9654C12.2669 12.0838 12.3773 12.1808 12.5062 12.2477C12.635 12.3146 12.7785 12.3492 12.9231 12.3485H14.4231L12.1154 14.3485L10.6154 15.5485L11.8154 12.7485C11.8708 12.6177 11.8923 12.4769 11.8785 12.3369C11.8646 12.1969 11.8154 12.0631 11.7354 11.9469C11.6554 11.8308 11.5477 11.7354 11.4215 11.6685C11.2954 11.6015 11.1554 11.5654 11.0115 11.5631H9.51154L11.1154 7.56308H13.6154L12.1154 11.1538Z" fill="black"/>
</mask>
<g mask="url(#mask0_4005_2129)">
<path d="M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z" fill="${color}" />
</g>
</svg>`;
    case 'electricPole':
      return `<svg viewBox="0 0 35 37" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path d="M17.5 2V35M2 6.95H33M3.55 3.65V6.95M9.75 3.65V6.95M25.25 3.65V6.95M31.45 3.65V6.95M28.35 6.95L17.5 18.5L6.65 6.95"
    stroke="${color}" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" />
</svg>`;
    case 'humane':
      return `<svg width="18" height="18" viewBox="0 0 18 21" fill="${color}" xmlns="http://www.w3.org/2000/svg" style="transform: translate(3px, 1.5px);">
<path d="M6 19.9502V18.4502C4.23333 17.8335 2.79167 16.7545 1.675 15.2132C0.558334 13.6719 0 11.9175 0 9.9502C0 8.7002 0.237667 7.52953 0.713 6.4382C1.18833 5.34686 1.82967 4.39686 2.637 3.5882C3.44433 2.77953 4.39433 2.13786 5.487 1.6632C6.57967 1.18853 7.74233 0.950863 8.975 0.950197C10.2077 0.94953 11.3743 1.1872 12.475 1.6632C13.5757 2.1392 14.534 2.78086 15.35 3.5882C16.166 4.39553 16.812 5.34553 17.288 6.4382C17.764 7.53086 18.0013 8.70153 18 9.9502C18 11.9169 17.4377 13.6629 16.313 15.1882C15.1883 16.7135 13.7507 17.7925 12 18.4252V19.9502C12 20.2335 11.904 20.4712 11.712 20.6632C11.52 20.8552 11.2827 20.9509 11 20.9502C10.7173 20.9495 10.48 20.8539 10.288 20.6632C10.096 20.4725 10 20.2349 10 19.9502V18.9002C9.83333 18.9335 9.66667 18.9502 9.5 18.9502H8.975C8.80833 18.9502 8.64567 18.9462 8.487 18.9382C8.32833 18.9302 8.166 18.9175 8 18.9002V19.9502C8 20.2335 7.904 20.4712 7.712 20.6632C7.52 20.8552 7.28267 20.9509 7 20.9502C6.71733 20.9495 6.48 20.8539 6.288 20.6632C6.096 20.4725 6 20.2349 6 19.9502ZM6 8.0002H12C12.2833 8.0002 12.521 7.90453 12.713 7.7132C12.905 7.52186 13.0007 7.2842 13 7.0002C12.9993 6.7162 12.9033 6.47886 12.712 6.2882C12.5207 6.09753 12.2833 6.00153 12 6.0002H6C5.71667 6.0002 5.47933 6.0962 5.288 6.2882C5.09667 6.4802 5.00067 6.71753 5 7.0002C4.99933 7.28286 5.09533 7.52053 5.288 7.7132C5.48067 7.90586 5.718 8.00153 6 8.0002ZM8 13.2502L7.5 13.7502C7.28333 13.9669 7.175 14.2169 7.175 14.5002C7.175 14.7835 7.28333 15.0335 7.5 15.2502C7.71667 15.4669 7.96667 15.5752 8.25 15.5752C8.53333 15.5752 8.78333 15.4669 9 15.2502L10.55 13.7002C10.75 13.5002 10.85 13.2669 10.85 13.0002C10.85 12.7335 10.75 12.5002 10.55 12.3002L10 11.7502L10.5 11.2502C10.7167 11.0335 10.825 10.7835 10.825 10.5002C10.825 10.2169 10.7167 9.96686 10.5 9.7502C10.2833 9.53353 10.0333 9.4252 9.75 9.4252C9.46667 9.4252 9.21667 9.53353 9 9.7502L7.45 11.3002C7.25 11.5002 7.15 11.7335 7.15 12.0002C7.15 12.2669 7.25 12.5002 7.45 12.7002L8 13.2502Z" fill="${color}"/>
</svg>`;
    case 'lamp':
      return `<rect x="8" y="4" width="8" height="12" fill="${color}" />
              <rect x="6" y="16" width="12" height="2" fill="${color}" />
              <circle cx="12" cy="6" r="2" fill="#fbbf24" stroke="${color}" stroke-width="1" />`;
    default:
      return `<circle cx="12" cy="12" r="8" fill="${color}" />`;
  }
};

const getElementIconName = (elementType: string): string => {
  switch (elementType) {
    case 'power-plant': return 'plug';
    case 'pole': return 'electricPole';
    case 'meter': return 'humane';
    case 'transformer': return 'plug';
    default: return 'lamp';
  }
};

const getElementBadgeHtml = (element: GridElement): string => {
  if (element.type !== 'meter') return '';
  if (element.isJouly) {
    return `<div style="position:absolute;top:-4px;right:-4px;width:12px;height:12px;background:#7c3aed;border-radius:50%;border:1px solid white;display:flex;align-items:center;justify-content:center;font-size:7px;color:white;">🔒</div>`;
  }
  if (getMeterLocalizationStatus(element) === 'unlocalized') {
    return `<div style="position:absolute;top:-4px;right:-4px;width:12px;height:12px;background:#f97316;border-radius:50%;border:1px solid white;display:flex;align-items:center;justify-content:center;font-size:7px;color:white;">!</div>`;
  }
  return '';
};

const getElementGlyphHtml = (element: GridElement, statusColor: string): string => {
  const iconName = getElementIconName(element.type);
  const iconSVG = getIconSVG(iconName, statusColor);
  return `
    <div style="position:relative;display:inline-block;">
      <svg width="24" height="24" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="12" fill="#ffffff" />
        <circle cx="12" cy="12" r="11" fill="none" stroke="${statusColor}" stroke-width="2" />
        ${iconSVG}
      </svg>
      ${getElementBadgeHtml(element)}
    </div>
  `;
};

// Construit le divIcon complet d'un élément (glyphe + bulle live intégrée).
// La bulle est intégrée dans l'icône (au lieu d'un tooltip permanent) pour que
// la mise à jour live ne soit qu'un setIcon ciblé, sans reconstruire les couches.
const buildElementIcon = (
  element: GridElement,
  live: MeterLiveMetric | undefined,
  vis: MetricsVisibility,
  isSelected: boolean,
): L.DivIcon => {
  const statusColor = getElementStatusColor(element, live, vis);
  const isUnlocalized = element.type === 'meter' && getMeterLocalizationStatus(element) === 'unlocalized';
  const bubbleHtml =
    !isSelected && element.type === 'meter' && vis.voltagePower ? buildMeterBubbleHtml(live, vis) : '';

  const selectedStyle = isSelected
    ? 'transform: scale(1.15); filter: drop-shadow(0 0 8px rgba(220, 38, 38, 0.85)) drop-shadow(0 2px 4px rgba(0,0,0,0.3));'
    : 'filter: drop-shadow(0 2px 4px rgba(0,0,0,0.3)) drop-shadow(0 1px 2px rgba(0,0,0,0.2));';
  const unlocalizedAnim = isUnlocalized ? 'animation: pulse-orange 1.5s ease-in-out infinite;' : '';

  return L.divIcon({
    className: `grid-element-marker${isUnlocalized ? ' meter-unlocalized' : ''}${element.isJouly ? ' meter-jouly' : ''}${isSelected ? ' grid-element-selected' : ''}`,
    html: `
      <div style="position:relative;width:32px;height:32px;display:flex;align-items:center;justify-content:center;user-select:none;transition:transform 0.15s ease;${selectedStyle}${unlocalizedAnim}">
        ${getElementGlyphHtml(element, statusColor)}
        ${bubbleHtml ? `<div style="position:absolute;top:calc(100% + 2px);left:50%;transform:translateX(-50%);pointer-events:none;">${bubbleHtml}</div>` : ''}
      </div>
    `,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
  });
};

const buildLightIcon = (
  light: any,
  live: LuminaireLiveMetric | undefined,
  vis: MetricsVisibility,
): L.DivIcon => {
  const baseColor = light.status === 'on' ? '#fbbf24' : light.status === 'off' ? '#6b7280' : '#f59e0b';
  const ringColor = vis.statusRing ? (live?.isOnline ? '#10b981' : '#ef4444') : '#ffffff';
  const bubbleHtml = buildLightBubbleHtml(live, vis);

  return L.divIcon({
    className: 'street-light-marker',
    html: `
      <div style="position:relative;width:12px;height:12px;">
        <div style="width:12px;height:12px;background-color:${baseColor};border:2px solid ${ringColor};border-radius:50%;box-shadow:0 2px 4px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;font-size:8px;">💡</div>
        ${bubbleHtml ? `<div style="position:absolute;left:calc(100% + 4px);top:50%;transform:translateY(-50%);pointer-events:none;">${bubbleHtml}</div>` : ''}
      </div>
    `,
    iconSize: [12, 12],
    iconAnchor: [6, 6],
  });
};

const buildLightTooltipHtml = (light: any, live: LuminaireLiveMetric | undefined, poleName: string): string => {
  const statusLabel = light.status === 'on' ? 'Allumé' : light.status === 'off' ? 'Éteint' : 'Maintenance';
  const livePowerLabel = formatLightPower(live?.power ?? null);
  const liveRssiLabel = live?.rssi !== undefined && live?.rssi !== null ? `${live.rssi.toFixed(0)} dBm` : null;
  const liveRow = (label: string, value: string) => `
    <div style="display:flex;justify-content:space-between;gap:8px;"><span>${label}</span><strong>${value}</strong></div>`;
  return `
    <div style="min-width:165px;border-radius:8px;border:1px solid #e2e8f0;background:#ffffff;box-shadow:0 6px 18px rgba(15,23,42,0.16);padding:8px 10px;font-size:11px;color:#334155;line-height:1.2;">
      <div style="font-weight:700;margin-bottom:4px;">Luminaire: ${escapeHtml(light.name || light.id)}</div>
      <div style="display:flex;justify-content:space-between;gap:8px;"><span>Statut</span><strong>${statusLabel}</strong></div>
      <div style="display:flex;justify-content:space-between;gap:8px;"><span>Puissance config.</span><strong>${escapeHtml(light.power)} W</strong></div>
      ${livePowerLabel ? liveRow('Puissance live', livePowerLabel) : ''}
      ${liveRssiLabel ? liveRow('RSSI', liveRssiLabel) : ''}
      ${light.zhagaDevui ? `<div style="display:flex;justify-content:space-between;gap:8px;"><span>Zhaga</span><strong style="font-family:monospace;">${escapeHtml(light.zhagaDevui)}</strong></div>` : ''}
      <div style="display:flex;justify-content:space-between;gap:8px;"><span>Poteau</span><strong>${escapeHtml(poleName)}</strong></div>
    </div>`;
};

const buildSelectedMeterTooltipHtml = (m: MeterRealtimeMetrics): string => `
  <div style="min-width:165px;border-radius:8px;border:1px solid #e2e8f0;background:#ffffff;box-shadow:0 6px 18px rgba(15,23,42,0.16);padding:8px 10px;font-size:11px;color:#334155;line-height:1.2;">
    <div style="font-weight:700;margin-bottom:4px;">Compteur: ${escapeHtml(m.deviceName)}</div>
    <div style="display:flex;justify-content:space-between;gap:8px;"><span>Tension</span><strong>${m.voltage.formattedValue} ${m.voltage.unit}</strong></div>
    <div style="display:flex;justify-content:space-between;gap:8px;"><span>Consommation</span><strong>${m.consumption.formattedValue} ${m.consumption.unit}</strong></div>
    <div style="display:flex;justify-content:space-between;gap:8px;"><span>Puissance</span><strong>${m.power.formattedValue} ${m.power.unit}</strong></div>
  </div>`;

const elementLatLng = (element: GridElement): [number, number] => [
  element.gpsCoordinates?.latitude || element.y / 100000,
  element.gpsCoordinates?.longitude || element.x / 100000,
];

// Signature live d'un compteur — permet de sauter les setIcon inutiles.
const meterLiveSignature = (live: MeterLiveMetric | undefined, vis: MetricsVisibility): string =>
  `${live?.isOnline ? 1 : 0}|${live?.voltage ?? ''}|${live?.power ?? ''}|${vis.voltagePower ? 1 : 0}|${vis.statusRing ? 1 : 0}`;

const lightLiveSignature = (live: LuminaireLiveMetric | undefined, vis: MetricsVisibility): string =>
  `${live?.isOnline ? 1 : 0}|${live?.power ?? ''}|${live?.rssi ?? ''}|${vis.voltagePower ? 1 : 0}|${vis.rssi ? 1 : 0}|${vis.statusRing ? 1 : 0}`;

interface LeafletMapProps {
  elements: GridElement[];
  connections: Connection[];
  selectedElement: GridElement | null;
  selectedConnection: Connection | null;
  meterMetrics: MeterRealtimeMetrics;
  meterLiveMetrics?: MeterLiveMetricsMap;
  luminaireLiveMetrics?: LuminaireLiveMetricsMap;
  metricsVisibility?: MetricsVisibility;
  onElementClick: (element: GridElement) => void;
  onConnectionClick: (connection: Connection) => void;
  onMapClick: (lat: number, lng: number) => void;
  onElementMove: (elementId: string, lat: number, lng: number) => void;
  onStreetLightClick?: (light: any, pole: GridElement) => void;
  center: [number, number];
  zoom: number;
  mapStyle: 'osm' | 'satellite' | 'terrain' | 'street';
  showGrid: boolean;
  className?: string;
  onMapReady?: () => void;
  isEditMode?: boolean;
  onVisibleDevicesChange?: (ids: Set<string>) => void;
}

export interface LeafletMapRef {
  setZoom: (zoom: number) => void;
  setView: (center: [number, number], zoom: number) => void;
  fitBoundsToElements: (elements: GridElement[]) => void;
}

// Gestion des événements de la carte
const MapEvents: React.FC<{
  onMapClick: (lat: number, lng: number) => void;
  isEditMode: boolean;
}> = ({ onMapClick, isEditMode }) => {
  useMapEvents({
    click: (e) => {
      if (!isEditMode) return;
      onMapClick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
};

// Icône de cluster personnalisée — taille/couleur selon la densité.
const createClusterIcon = (cluster: L.MarkerCluster): L.DivIcon => {
  const count = cluster.getChildCount();
  let size = 36;
  let bg = '#3b82f6';
  if (count >= 100) { size = 52; bg = '#1e3a8a'; }
  else if (count >= 25) { size = 46; bg = '#1d4ed8'; }
  else if (count >= 10) { size = 40; bg = '#2563eb'; }
  return L.divIcon({
    html: `<div style="width:${size}px;height:${size}px;display:flex;align-items:center;justify-content:center;background:${bg};color:#fff;border:2px solid rgba(255,255,255,0.9);border-radius:50%;font-weight:700;font-size:13px;box-shadow:0 2px 6px rgba(0,0,0,0.35);">${count}</div>`,
    className: 'grid-cluster-icon',
    iconSize: L.point(size, size),
  });
};

// ---------------------------------------------------------------------------
// Couche unique impérative: clustering + bulles live intégrées + connexions.
// ---------------------------------------------------------------------------
const MapLayers: React.FC<{
  elements: GridElement[];
  connections: Connection[];
  selectedElement: GridElement | null;
  selectedConnection: Connection | null;
  meterMetrics: MeterRealtimeMetrics;
  meterLiveMetrics: MeterLiveMetricsMap;
  luminaireLiveMetrics: LuminaireLiveMetricsMap;
  metricsVisibility: MetricsVisibility;
  isEditMode: boolean;
  onElementClick: (element: GridElement) => void;
  onElementMove: (elementId: string, lat: number, lng: number) => void;
  onConnectionClick: (connection: Connection) => void;
  onStreetLightClick: (light: any, pole: GridElement) => void;
  onVisibleDevicesChange?: (ids: Set<string>) => void;
}> = ({
  elements,
  connections,
  selectedElement,
  selectedConnection,
  meterMetrics,
  meterLiveMetrics,
  luminaireLiveMetrics,
  metricsVisibility,
  isEditMode,
  onElementClick,
  onElementMove,
  onConnectionClick,
  onStreetLightClick,
  onVisibleDevicesChange,
}) => {
  const map = useMap();

  // Refs vers les données live et callbacks: le build n'en dépend pas (pas de
  // reconstruction sur chaque tick), les effets live les lisent à jour.
  const meterLiveRef = useRef(meterLiveMetrics);
  const luminaireLiveRef = useRef(luminaireLiveMetrics);
  const visRef = useRef(metricsVisibility);
  const meterMetricsRef = useRef(meterMetrics);
  const selectedIdRef = useRef<string | null>(selectedElement?.id ?? null);
  const onElementClickRef = useRef(onElementClick);
  const onElementMoveRef = useRef(onElementMove);
  const onStreetLightClickRef = useRef(onStreetLightClick);
  const onConnectionClickRef = useRef(onConnectionClick);
  meterLiveRef.current = meterLiveMetrics;
  luminaireLiveRef.current = luminaireLiveMetrics;
  visRef.current = metricsVisibility;
  meterMetricsRef.current = meterMetrics;
  selectedIdRef.current = selectedElement?.id ?? null;
  onElementClickRef.current = onElementClick;
  onElementMoveRef.current = onElementMove;
  onStreetLightClickRef.current = onStreetLightClick;
  onConnectionClickRef.current = onConnectionClick;

  // Couches & registres persistants
  const pointsLayerRef = useRef<L.LayerGroup | null>(null);
  const connectionLayerRef = useRef<L.LayerGroup | null>(null);
  const rendererRef = useRef<L.Canvas | null>(null);
  const elementMarkersRef = useRef<Map<string, L.Marker>>(new Map());
  const lightMarkersRef = useRef<Map<string, L.Marker>>(new Map());
  const lightPoleRef = useRef<Map<string, { light: any; pole: GridElement }>>(new Map());
  const connectionLinesRef = useRef<Map<string, L.Polyline>>(new Map());
  const elementsByIdRef = useRef<Map<string, GridElement>>(new Map());

  // Renderer canvas partagé pour les polylignes de connexion.
  // NB: pas de pane custom sur les marqueurs — markercluster gère lui-même les
  // panes pendant ses animations de zoom/spiderfy et un pane custom y provoque
  // un crash (_initIcon → appendChild de undefined). Le z-ordering de la
  // sélection passe uniquement par setZIndexOffset.
  useEffect(() => {
    rendererRef.current = L.canvas({ padding: 0.5 });
  }, [map]);

  // Recompose l'icône d'un compteur depuis les refs (live + sélection).
  const refreshElementMarker = useCallback((id: string) => {
    const marker = elementMarkersRef.current.get(id);
    const element = elementsByIdRef.current.get(id);
    if (!marker || !element) return;
    const live = element.type === 'meter' ? meterLiveRef.current.get(id) : undefined;
    const isSelected = selectedIdRef.current === id;
    marker.setIcon(buildElementIcon(element, live, visRef.current, isSelected));
    marker.setZIndexOffset(isSelected ? 1000 : 0);

    // Tooltip détaillé uniquement pour le compteur sélectionné connu.
    const m = meterMetricsRef.current;
    const wantTooltip = isSelected && element.type === 'meter' && m.hasDeviceName;
    if (wantTooltip) {
      marker.bindTooltip(buildSelectedMeterTooltipHtml(m), {
        direction: 'top', offset: [0, -16], permanent: true, className: 'node-metrics-tooltip',
      }).openTooltip();
    } else if (marker.getTooltip()) {
      marker.unbindTooltip();
    }
  }, []);

  const refreshLightMarker = useCallback((id: string) => {
    const marker = lightMarkersRef.current.get(id);
    const entry = lightPoleRef.current.get(id);
    if (!marker || !entry) return;
    const live = luminaireLiveRef.current.get(id);
    marker.setIcon(buildLightIcon(entry.light, live, visRef.current));
    // Garder le tooltip survol (Puissance live / RSSI) synchronisé avec le live.
    if (marker.getTooltip()) {
      marker.setTooltipContent(buildLightTooltipHtml(entry.light, live, entry.pole.name));
    }
  }, []);

  // Calcule l'ensemble des devices (compteurs + luminaires) actuellement affichés
  // comme marqueur INDIVIDUEL (dans la vue ET hors cluster), et le remonte. Sert à
  // ne collecter les métriques live que pour ces devices: un device dans un cluster
  // ou hors écran n'est pas visible, donc inutile de l'interroger.
  const onVisibleRef = useRef(onVisibleDevicesChange);
  onVisibleRef.current = onVisibleDevicesChange;
  const computeVisibleDevices = useCallback(() => {
    const cb = onVisibleRef.current;
    if (!cb) return;
    const layer = pointsLayerRef.current as any;
    if (!layer) return;
    let bounds: L.LatLngBounds;
    try {
      bounds = map.getBounds();
    } catch {
      return;
    }
    const isIndividuallyVisible = (marker: L.Marker): boolean => {
      try {
        if (typeof layer.getVisibleParent === 'function') {
          // markercluster: visible individuellement si son parent visible est lui-même.
          return layer.getVisibleParent(marker) === marker;
        }
      } catch {
        /* repli sur le test de bounds ci-dessous */
      }
      return bounds.contains(marker.getLatLng());
    };
    const ids = new Set<string>();
    elementMarkersRef.current.forEach((marker, id) => {
      if (isIndividuallyVisible(marker)) ids.add(id);
    });
    lightMarkersRef.current.forEach((marker, id) => {
      if (isIndividuallyVisible(marker)) ids.add(id);
    });
    cb(ids);
  }, [map]);

  // Recalcul (débouncé) à chaque déplacement/zoom (le clustering change alors).
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const schedule = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(computeVisibleDevices, 300);
    };
    map.on('moveend', schedule);
    map.on('zoomend', schedule);
    schedule();
    return () => {
      if (timer) clearTimeout(timer);
      map.off('moveend', schedule);
      map.off('zoomend', schedule);
    };
  }, [map, computeVisibleDevices]);

  // (Re)construction structurelle des points (éléments + luminaires).
  // Déps: elements, isEditMode uniquement → AUCUNE reconstruction sur tick live.
  useEffect(() => {
    // (Re)créer la couche de points selon le mode (cluster en lecture, simple en édition).
    if (pointsLayerRef.current) {
      map.removeLayer(pointsLayerRef.current);
      pointsLayerRef.current = null;
    }
    elementMarkersRef.current.clear();
    lightMarkersRef.current.clear();
    lightPoleRef.current.clear();
    elementsByIdRef.current.clear();

    const layer: L.LayerGroup = isEditMode
      ? L.layerGroup()
      : (L as any).markerClusterGroup({
          chunkedLoading: true,
          removeOutsideVisibleBounds: true,
          spiderfyOnMaxZoom: true,
          showCoverageOnHover: false,
          zoomToBoundsOnClick: true,
          maxClusterRadius: 50,
          spiderfyDistanceMultiplier: 1.6,
          iconCreateFunction: createClusterIcon,
        });
    pointsLayerRef.current = layer;

    const visibleElements = elements.filter(
      (el) => el && el.action !== ElementAction.TO_DELETE && el.id && el.type,
    );

    visibleElements.forEach((element) => {
      elementsByIdRef.current.set(element.id, element);
      const [lat, lng] = elementLatLng(element);
      const isSelected = selectedIdRef.current === element.id;
      const marker = L.marker([lat, lng], {
        draggable: isEditMode,
        riseOnHover: true,
        riseOffset: 600,
        icon: buildElementIcon(
          element,
          element.type === 'meter' ? meterLiveRef.current.get(element.id) : undefined,
          visRef.current,
          isSelected,
        ),
      });
      marker.on('click', () => onElementClickRef.current(element));
      if (isEditMode) {
        marker.on('dragend', (e) => {
          const pos = (e.target as L.Marker).getLatLng();
          onElementMoveRef.current(element.id, pos.lat, pos.lng);
        });
      }
      elementMarkersRef.current.set(element.id, marker);
      layer.addLayer(marker);
      if (isSelected) refreshElementMarker(element.id);

      // Luminaires du poteau
      if (element.type === 'pole' && element.lights && element.lights.length > 0) {
        const [poleLat, poleLng] = elementLatLng(element);
        element.lights
          .filter((light: any) => light.action !== ElementAction.TO_DELETE)
          .forEach((light: any, index: number) => {
            const offsetLat = poleLat + index * 0.0001;
            const offsetLng = poleLng + index * 0.0001;
            lightPoleRef.current.set(light.id, { light, pole: element });
            const lightMarker = L.marker([offsetLat, offsetLng], {
              riseOnHover: true,
              icon: buildLightIcon(light, luminaireLiveRef.current.get(light.id), visRef.current),
            });
            lightMarker.on('click', (e) => {
              (e as any).originalEvent?.stopPropagation();
              onStreetLightClickRef.current(light, element);
            });
            lightMarker.bindTooltip(
              buildLightTooltipHtml(light, luminaireLiveRef.current.get(light.id), element.name),
              { className: 'node-metrics-tooltip', direction: 'top', offset: [0, -6], sticky: false },
            );
            lightMarkersRef.current.set(light.id, lightMarker);
            layer.addLayer(lightMarker);
          });
      }
    });

    map.addLayer(layer);
    // Recalcul de l'ensemble des devices visibles après (re)construction/clustering.
    const visTimer = setTimeout(computeVisibleDevices, 350);

    return () => {
      clearTimeout(visTimer);
      if (pointsLayerRef.current) {
        map.removeLayer(pointsLayerRef.current);
        pointsLayerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, elements, isEditMode, refreshElementMarker]);

  // Mise à jour live des compteurs — setIcon ciblé, sans reconstruire les couches.
  useEffect(() => {
    elementMarkersRef.current.forEach((marker, id) => {
      const element = elementsByIdRef.current.get(id);
      if (!element || element.type !== 'meter') return;
      const sig = meterLiveSignature(meterLiveMetrics.get(id), metricsVisibility);
      if ((marker as any)._liveSig === sig) return;
      (marker as any)._liveSig = sig;
      // Toujours rafraîchir (y compris le sélectionné): l'anneau online/offline
      // vient de meterLiveMetrics, pas de meterMetrics. refreshElementMarker gère
      // en interne la bascule bulle live / tooltip détaillé.
      refreshElementMarker(id);
    });
  }, [meterLiveMetrics, metricsVisibility, refreshElementMarker]);

  // Mise à jour live des luminaires.
  useEffect(() => {
    lightMarkersRef.current.forEach((marker, id) => {
      const sig = lightLiveSignature(luminaireLiveMetrics.get(id), metricsVisibility);
      if ((marker as any)._liveSig === sig) return;
      (marker as any)._liveSig = sig;
      refreshLightMarker(id);
    });
  }, [luminaireLiveMetrics, metricsVisibility, refreshLightMarker]);

  // Mise à jour de la sélection — uniquement l'ancien et le nouveau marqueur.
  const prevSelectedRef = useRef<string | null>(null);
  useEffect(() => {
    const prev = prevSelectedRef.current;
    const next = selectedElement?.id ?? null;
    if (prev === next) return;
    prevSelectedRef.current = next;
    if (prev) {
      const m = elementMarkersRef.current.get(prev);
      if (m) refreshElementMarker(prev);
    }
    if (next) {
      const m = elementMarkersRef.current.get(next);
      if (m) {
        refreshElementMarker(next);
        m.setZIndexOffset(1000);
        // Révéler le marqueur s'il est caché dans un cluster (sinon la sélection
        // d'un élément clusterisé serait invisible). zoomToShowLayer ne bouge la
        // vue que si nécessaire et appelle le callback immédiatement sinon.
        const cluster = pointsLayerRef.current as any;
        if (
          cluster &&
          typeof cluster.zoomToShowLayer === 'function' &&
          typeof cluster.hasLayer === 'function' &&
          cluster.hasLayer(m)
        ) {
          cluster.zoomToShowLayer(m, () => {
            refreshElementMarker(next);
            m.setZIndexOffset(1000);
          });
        }
      }
    }
  }, [selectedElement, refreshElementMarker]);

  // Mise à jour du tooltip détaillé du compteur sélectionné quand ses métriques changent.
  useEffect(() => {
    const id = selectedElement?.id;
    if (id && selectedElement?.type === 'meter') refreshElementMarker(id);
  }, [meterMetrics, selectedElement, refreshElementMarker]);

  // (Re)construction des connexions (canvas). Déps structurelles uniquement.
  useEffect(() => {
    if (connectionLayerRef.current) {
      map.removeLayer(connectionLayerRef.current);
    }
    const group = L.layerGroup();
    connectionLayerRef.current = group;
    const lines = connectionLinesRef.current;
    lines.clear();

    const elementById = new Map(elements.map((el) => [el.id, el] as const));
    connections.forEach((connection) => {
      const fromElement = elementById.get(connection.fromId);
      const toElement = elementById.get(connection.toId);
      let fromLat = 0, fromLng = 0, toLat = 0, toLng = 0;
      let canRender = false;
      if (fromElement && toElement) {
        [fromLat, fromLng] = elementLatLng(fromElement);
        [toLat, toLng] = elementLatLng(toElement);
        canRender = true;
      } else if ((connection as any).fromCoordinates && (connection as any).toCoordinates) {
        fromLat = (connection as any).fromCoordinates.latitude;
        fromLng = (connection as any).fromCoordinates.longitude;
        toLat = (connection as any).toCoordinates.latitude;
        toLng = (connection as any).toCoordinates.longitude;
        canRender = true;
      }
      if (!canRender) return;

      const isSelected = selectedConnection?.id === connection.id;
      const polyline = L.polyline([[fromLat, fromLng], [toLat, toLng]], {
        color: isSelected ? '#ef4444' : '#3b82f6',
        weight: isSelected ? 5 : 3,
        opacity: 0.7,
        dashArray: connection.type === 'data-line' ? '5,5' : undefined,
        renderer: rendererRef.current ?? undefined,
        interactive: true,
      });
      polyline.on('click', (e) => {
        (e as any).originalEvent?.stopPropagation();
        onConnectionClickRef.current(connection);
      });
      polyline.bindPopup(`
        <div>
          <strong>Connexion ${escapeHtml(connection.id)}</strong><br/>
          Nom: ${escapeHtml(connection.name || 'Non défini')}<br/>
          Type: ${escapeHtml(connection.type)}<br/>
          Tension: ${escapeHtml(connection.voltage)}V<br/>
          Résistance: ${escapeHtml(connection.resistance)}Ω<br/>
          Longueur: ${escapeHtml(connection.length)}m
        </div>
      `);
      lines.set(connection.id, polyline);
      group.addLayer(polyline);
    });

    map.addLayer(group);

    return () => {
      if (connectionLayerRef.current) {
        map.removeLayer(connectionLayerRef.current);
        connectionLayerRef.current = null;
      }
      lines.clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, connections, elements]);

  // Mise en évidence de la connexion sélectionnée — style uniquement.
  useEffect(() => {
    connectionLinesRef.current.forEach((line, id) => {
      const isSelected = selectedConnection?.id === id;
      line.setStyle({ color: isSelected ? '#ef4444' : '#3b82f6', weight: isSelected ? 5 : 3 });
    });
  }, [selectedConnection]);

  return null;
};

export const LeafletMap = forwardRef<LeafletMapRef, LeafletMapProps>(({
  elements,
  connections,
  selectedElement,
  selectedConnection,
  meterMetrics,
  meterLiveMetrics,
  luminaireLiveMetrics,
  metricsVisibility = DEFAULT_METRICS_VISIBILITY,
  onElementClick,
  onConnectionClick,
  onMapClick,
  onElementMove,
  onStreetLightClick,
  center,
  zoom,
  mapStyle,
  showGrid,
  className = '',
  onMapReady,
  isEditMode = false,
  onVisibleDevicesChange,
}, ref) => {
  const resolvedMeterLive: MeterLiveMetricsMap = meterLiveMetrics ?? EMPTY_METER_LIVE_MAP;
  const resolvedLuminaireLive: LuminaireLiveMetricsMap = luminaireLiveMetrics ?? EMPTY_LUMINAIRE_LIVE_MAP;
  const mapRef = useRef<L.Map>(null);

  const fitBoundsToElements = useCallback((els: GridElement[]) => {
    if (!mapRef.current) return;
    const latLngs = els
      .filter(
        (el) =>
          el.gpsCoordinates?.latitude &&
          el.gpsCoordinates?.longitude &&
          el.gpsCoordinates.latitude !== 0 &&
          el.gpsCoordinates.longitude !== 0 &&
          !isNaN(el.gpsCoordinates.latitude) &&
          !isNaN(el.gpsCoordinates.longitude),
      )
      .map((el) => [el.gpsCoordinates!.latitude, el.gpsCoordinates!.longitude] as L.LatLngTuple);
    if (latLngs.length === 0) return;
    mapRef.current.fitBounds(L.latLngBounds(latLngs), { padding: [60, 60], maxZoom: 18 });
  }, []);

  useImperativeHandle(ref, () => ({
    setZoom: (newZoom: number) => {
      mapRef.current?.setZoom(newZoom);
    },
    setView: (newCenter: [number, number], newZoom: number) => {
      mapRef.current?.setView(newCenter, newZoom);
    },
    fitBoundsToElements,
  }));

  useEffect(() => {
    if (mapRef.current && onMapReady) onMapReady();
  }, [onMapReady]);

  const getTileLayerUrl = () => {
    switch (mapStyle) {
      case 'satellite':
        return 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';
      case 'terrain':
        return 'https://tile.opentopomap.org/{z}/{x}/{y}.png';
      case 'street':
        return 'https://cartodb-basemaps-a.global.ssl.fastly.net/light_all/{z}/{x}/{y}.png';
      case 'osm':
      default:
        return 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';
    }
  };

  const getTileLayerAttribution = () => {
    switch (mapStyle) {
      case 'satellite':
        return '&copy; <a href="https://www.esri.com/">Esri</a> &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community';
      case 'terrain':
        return '&copy; <a href="https://opentopomap.org/">OpenTopoMap</a> contributors';
      case 'street':
        return '&copy; <a href="https://carto.com/">CARTO</a> &mdash; &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';
      case 'osm':
      default:
        return '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';
    }
  };

  return (
    <div className={`leaflet-map-container ${className}`}>
      <MapContainer
        center={center}
        zoom={zoom}
        style={{ height: '100%', width: '100%' }}
        ref={mapRef}
        preferCanvas
      >
        <TileLayer
          url={getTileLayerUrl()}
          attribution={getTileLayerAttribution()}
          updateWhenIdle
          updateWhenZooming={false}
          keepBuffer={4}
        />

        <MapEvents onMapClick={onMapClick} isEditMode={isEditMode} />

        <MapLayers
          elements={elements}
          connections={connections}
          selectedElement={selectedElement}
          selectedConnection={selectedConnection}
          meterMetrics={meterMetrics}
          meterLiveMetrics={resolvedMeterLive}
          luminaireLiveMetrics={resolvedLuminaireLive}
          metricsVisibility={metricsVisibility}
          isEditMode={isEditMode}
          onElementClick={onElementClick}
          onElementMove={onElementMove}
          onConnectionClick={onConnectionClick}
          onStreetLightClick={onStreetLightClick || (() => {})}
          onVisibleDevicesChange={onVisibleDevicesChange}
        />
      </MapContainer>

      {/* Bouton recentrer sur les éléments (best practice: "reset/return to view") */}
      <button
        type="button"
        className="leaflet-fit-btn"
        title="Recentrer sur les éléments"
        onClick={() => fitBoundsToElements(elements)}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 7V3h4M17 3h4v4M21 17v4h-4M7 21H3v-4" />
          <circle cx="12" cy="12" r="3" />
        </svg>
      </button>

      {/* Légende des types / états */}
      <div className="leaflet-legend">
        <div className="leaflet-legend-title">Légende</div>
        <div className="leaflet-legend-row"><span className="lg-dot" style={{ background: '#10b981' }} />Actif / en ligne</div>
        <div className="leaflet-legend-row"><span className="lg-dot" style={{ background: '#ef4444' }} />Hors ligne</div>
        <div className="leaflet-legend-row"><span className="lg-dot" style={{ background: '#7c3aed' }} />Compteur Jouly</div>
        <div className="leaflet-legend-row"><span className="lg-dot" style={{ background: '#f97316' }} />Non localisé</div>
        <div className="leaflet-legend-row"><span className="lg-cluster">12</span>Groupe (cliquer pour étaler)</div>
      </div>
    </div>
  );
});
